import React, { useState, useCallback } from "react";
import { useSimpleDataLoader } from "../hooks/useSimpleDataLoader";
import SimpleSearchFilter from "../components/SimpleSearchFilter";
import DealsListAuto from "../components/DealsListAuto";
import DataLoadSettings from "../components/DataLoadSettings";

const SimpleReportsPage = () => {
  const { allData, loading, error, lastLoaded, fieldMetadata, refreshData } = useSimpleDataLoader();
  const [filteredData, setFilteredData] = useState([]);
  const [selectedFields, setSelectedFields] = useState(['ufCrm3_1746789101', 'createdTime']); // По умолчанию

  const handleFilteredData = useCallback((data) => {
    setFilteredData(data);
  }, []);

  const handleSelectedFields = useCallback((fields) => {
    setSelectedFields(fields);
  }, []);

  const handleRetry = useCallback(() => {
    refreshData();
  }, [refreshData]);

  const handleLoadData = useCallback((dateRange) => {
    refreshData(dateRange);
  }, [refreshData]);

  // Компонент загрузки
  if (loading && allData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Загружаем данные из Битрикс24...
            </h2>
            <p className="text-gray-600">
              Это может занять некоторое время при первой загрузке
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Заголовок страницы */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Отчет
              </h1>
              <p className="text-gray-600">
                
              </p>
              {lastLoaded && (
                <p className="text-sm text-gray-500 mt-1">
                  Последнее обновление: {lastLoaded.toLocaleString('ru-RU')}
                </p>
              )}
            </div>
            
            <div className="flex space-x-3">
              <DataLoadSettings 
                onLoadData={handleLoadData}
                loading={loading}
              />
              
              <button
                onClick={() => refreshData()}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline mr-2"></div>
                    Обновление...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Быстрое обновление
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Ошибка загрузки */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Ошибка загрузки данных</h3>
                <p className="text-sm text-red-600 mt-1">{error}</p>
                <button
                  onClick={handleRetry}
                  className="mt-2 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded transition-colors"
                >
                  Попробовать снова
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Статистика загрузки */}
        {allData.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{allData.length}</div>
                  <div className="text-sm text-gray-500">Всего записей</div>
                </div>
                
                {loading && (
                  <div className="flex items-center text-blue-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-sm">Обновление...</span>
                  </div>
                )}
              </div>
              
              
            </div>
          </div>
        )}

        {/* Фильтры и поиск */}
        {allData.length > 0 && (
          <SimpleSearchFilter 
            data={allData}
            fieldMetadata={fieldMetadata}
            onFilteredData={handleFilteredData}
            onSelectedFields={handleSelectedFields}
          />
        )}

        {/* Результаты */}
        {allData.length > 0 ? (
          <DealsListAuto
            deals={filteredData}
            loading={false}
            error={null}
            totalCount={filteredData.length}
            selectedFields={selectedFields}
            fieldMetadata={fieldMetadata}
            onRetry={handleRetry}
            showExportButton={true}
          />
        ) : !loading && !error && (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Нет данных</h3>
            <p className="text-gray-500 mb-4">
              Данные не загружены. Попробуйте обновить страницу или проверьте подключение к Битрикс24.
            </p>
            <button
              onClick={refreshData}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Загрузить данные
            </button>
          </div>
        )}

        {/* Дополнительная информация */}
        
      </div>
    </div>
  );
};

export default SimpleReportsPage;